import { useEffect } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useCartesiaDictionary, useElevenLabsDictionary } from '@/hooks/usePronunciationDictionary';
import { CartesiaDictionary, ElevenLabsDictionary } from '@/shared/types/integration';

interface PronunciationDictionaryToggleProps {
  provider: 'cartesia' | 'elevenlabs';
  name: string;
  // ElevenLabs requires a pinned version_id alongside the dict id; pass the
  // sibling form-field name and the toggle keeps it in sync with the dict's
  // current latest_version_id.
  versionName?: string;
}

const isElevenLabsDict = (
  d: CartesiaDictionary | ElevenLabsDictionary
): d is ElevenLabsDictionary => 'latest_version_id' in d;

const PronunciationDictionaryToggle = ({
  provider,
  name,
  versionName,
}: PronunciationDictionaryToggleProps) => {
  const { t } = useTranslation();
  const { setValue, control, getValues } = useFormContext();
  const fieldValue = useWatch({ control, name }) as string | undefined;

  const cartesia = useCartesiaDictionary(provider === 'cartesia');
  const elevenlabs = useElevenLabsDictionary(provider === 'elevenlabs');
  const { data, isLoading } = provider === 'cartesia' ? cartesia : elevenlabs;

  const dictionary = data?.dictionary ?? undefined;
  const checked = !!fieldValue && !!dictionary && fieldValue === dictionary.id;

  // Keep version_id in sync with the dict's latest_version_id while the toggle
  // is on, so post-save dict updates surface as a dirty form rather than a
  // stale pinned version (which ElevenLabs would reject silently).
  useEffect(() => {
    if (!versionName || !checked || !dictionary) {
      return;
    }
    const latest = isElevenLabsDict(dictionary) ? dictionary.latest_version_id : undefined;
    if (!latest) {
      return;
    }
    if (getValues(versionName) !== latest) {
      setValue(versionName, latest, { shouldDirty: true });
    }
  }, [versionName, checked, dictionary, getValues, setValue]);

  const handleChange = (next: boolean) => {
    if (next && dictionary) {
      setValue(name, dictionary.id, { shouldDirty: true });
      if (versionName) {
        const latest = isElevenLabsDict(dictionary) ? dictionary.latest_version_id : undefined;
        setValue(versionName, latest ?? undefined, { shouldDirty: true });
      }
    } else {
      setValue(name, undefined, { shouldDirty: true });
      if (versionName) {
        setValue(versionName, undefined, { shouldDirty: true });
      }
    }
  };

  if (isLoading) {
    return <Skeleton className="ml-auto h-5 w-9" />;
  }

  return (
    <Switch
      label={t('integrations.voice_common.voice.pronunciation_dictionary.label')}
      description={
        dictionary
          ? t('integrations.voice_common.voice.pronunciation_dictionary.description')
          : t('integrations.voice_common.voice.pronunciation_dictionary.not_found')
      }
      variant="description"
      switchPosition="right"
      checked={checked}
      disabled={!dictionary}
      onCheckedChange={handleChange}
    />
  );
};

export default PronunciationDictionaryToggle;
